import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useAppKitAccount,
  useAppKitProvider,
} from '@reown/appkit/react'
import type { Eip1193Provider } from 'ethers'
import { Header } from './components/Header.js'
import { SettingsCard } from './components/SettingsCard.js'
import { DAppsModal } from './components/DAppsModal.js'
import { LandingCard } from './components/LandingCard.js'
import { OnboardingCard } from './components/OnboardingCard.js'
import { PortfolioCard } from './components/PortfolioCard.js'
import { ProposalCard } from './components/ProposalCard.js'
import { RequestCard } from './components/RequestCard.js'
import { SummaryCard } from './components/SummaryCard.js'
import { TransactionsCard } from './components/TransactionsCard.js'
import { PendingTxList } from './components/PendingTxList.js'
import { usePortfolio } from './hooks/usePortfolio.js'
import { usePositions } from './hooks/usePositions.js'
import { useTransactions } from './hooks/useTransactions.js'
import {
  useWalletKitState,
  type Request as WCRequest,
} from './hooks/useWalletKit.js'
import { usePending } from './hooks/usePending.js'
import { loadSettings, saveSettings } from './storage.js'
import { resolveSafe, type ResolvedSafe } from './lib/resolveSafe.js'
import { useSafeProbe } from './hooks/useSafeProbe.js'
import {
  handleDAppPersonalSign,
  handleDAppSignTypedData,
  handleDAppTransaction,
  handleSendCalls,
  SignMessagePolicyRefused,
} from './lib/handler.js'
import type { BatchCall } from './lib/safe.js'
import type { EthSignTypedDataPayload } from './lib/signMessage.js'
import { getWalletKit } from './lib/walletkit.js'
import { SafewardClient } from './safeward-client.js'
import type { AppSettings } from './types.js'

export function App() {
  const [settings, setSettings] = useState<AppSettings>(() => loadSettings())
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [dappsOpen, setDappsOpen] = useState(false)

  // Resolve `?safe=` into either the constants pair we know, "onboarding"
  // for an unrecognised Safe, or "landing" for the bare URL. The probe runs
  // for unrecognised Safes and may upgrade them to "safeward" by spotting a
  // module that's a Safe with the Guardian as an owner — that lets us route
  // to any SafeWard-enabled Safe, not just the constants pair.
  const inputResolved = useMemo(
    () => resolveSafe(settings.moduleSafe),
    [settings.moduleSafe],
  )

  // Track a probe-discovered Module Safe address separately from settings so
  // it doesn't get persisted to localStorage but does drive /v1/pending.
  const [discoveredModuleSafe, setDiscoveredModuleSafe] = useState('')
  useEffect(() => {
    // Reset discovered pair whenever the user navigates to a different
    // input — otherwise stale discovery would override the new input.
    setDiscoveredModuleSafe('')
  }, [inputResolved])

  const effectiveModuleSafe =
    inputResolved.kind === 'safeward'
      ? inputResolved.moduleSafe
      : discoveredModuleSafe
  const effectiveSettings = useMemo<AppSettings>(
    () => ({ ...settings, moduleSafe: effectiveModuleSafe }),
    [settings, effectiveModuleSafe],
  )

  const wk = useWalletKitState()
  const pending = usePending(effectiveSettings)
  const account = useAppKitAccount()
  const { walletProvider } = useAppKitProvider<Eip1193Provider>('eip155')

  // Probe runs for any address resolveSafe couldn't classify against the
  // constants. Needs the Guardian (from /v1/health) to detect SafeWard via
  // module-ownership, so it re-fires once that arrives.
  const probeAddress =
    inputResolved.kind === 'onboarding' ? inputResolved.input : null
  const probe = useSafeProbe(probeAddress, pending.guardianAddress)
  useEffect(() => {
    if (probe.status === 'done' && probe.result.kind === 'safeward-main') {
      setDiscoveredModuleSafe(probe.result.moduleSafe)
    }
  }, [probe])

  const resolved = useMemo<ResolvedSafe>(() => {
    if (inputResolved.kind !== 'onboarding') return inputResolved
    if (probe.status === 'done' && probe.result.kind === 'safeward-main') {
      return {
        kind: 'safeward',
        mainSafe: inputResolved.input,
        moduleSafe: probe.result.moduleSafe,
      }
    }
    return inputResolved
  }, [inputResolved, probe])

  // Portfolio + tx history fire only when we're rendering a real dashboard
  // (i.e. resolved to safeward). Hooks always run — they just stay idle
  // when address is null.
  const dashboardAddress =
    resolved.kind === 'safeward' ? resolved.mainSafe : null
  const portfolio = usePortfolio(effectiveSettings, dashboardAddress)
  const transactions = useTransactions(effectiveSettings, dashboardAddress)
  // Lazy: only fetches when the user opens the positions expander. Gated
  // because per-token responses can be multi-MB on whale wallets.
  const [positionsOpen, setPositionsOpen] = useState(false)
  const positions = usePositions(
    effectiveSettings,
    dashboardAddress,
    positionsOpen,
  )

  // Surface incoming requests in the page title and via auto-scroll so the
  // user notices them even if they're focused on the dApp tab. dApps like
  // Jumper time out fast (~25s); a missed notification = a failed swap.
  useEffect(() => {
    const n = wk.pendingRequests.length
    document.title = n > 0 ? `🔔 (${n}) SafeWard` : 'SafeWard · Guardian Dashboard'
    if (n > 0) {
      const el = document.getElementById('incoming-anchor')
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [wk.pendingRequests.length])

  const onSaveSettings = useCallback((next: AppSettings) => {
    saveSettings(next)
    setSettings(next)
    setSettingsOpen(false)
  }, [])

  const onApproveRequest = useCallback(
    async (req: WCRequest) => {
      const { topic, params, id } = req
      const method = params.request.method
      const wkInstance = await getWalletKit()

      try {
        if (!walletProvider) throw new Error('No signer connected — pair Rabby first')
        if (!account.address) throw new Error('No signer address available')

        const deps = {
          provider: walletProvider,
          signerAddress: account.address,
          safeward: new SafewardClient({
            baseUrl: settings.safewardUrl,
            authToken: settings.safewardAuthToken,
          }),
        }
        const tApprove = Date.now()
        console.log('[SafeWard] user approved, starting handler for', method)

        let response: { result: unknown } | { error: { code: number; message: string } }

        if (method === 'eth_sendTransaction') {
          const tx = (params.request.params?.[0] ?? {}) as {
            to?: string
            value?: string
            data?: `0x${string}`
          }
          if (!tx.to) throw new Error('Request missing `to` address')
          const result = await handleDAppTransaction(
            { to: tx.to, value: tx.value, data: tx.data },
            deps,
          )
          response = { result: result.safeTxHash }
        } else if (method === 'wallet_sendCalls') {
          const arg = (params.request.params?.[0] ?? {}) as {
            from?: string
            chainId?: string
            calls?: Array<{ to: string; value?: string; data?: `0x${string}` }>
            atomicRequired?: boolean
          }
          if (!arg.calls?.length) throw new Error('wallet_sendCalls: empty calls')
          const calls: BatchCall[] = arg.calls.map((c) => ({
            to: c.to,
            value: c.value ? BigInt(c.value) : 0n,
            data: (c.data ?? '0x') as `0x${string}`,
            operation: 0,
          }))
          const result = await handleSendCalls(calls, deps)
          // EIP-5792 v2.0.0 expects { id }; viem accepts both string and object.
          response = { result: { id: result.safeTxHash } }
        } else if (method === 'personal_sign' || method === 'eth_signTypedData_v4') {
          // Off-chain EIP-1271. Ainura signs the SafeMessage hash on the
          // Module Safe's domain via wallet, the worker co-signs with
          // Guardian's key (after running the smart-permit policy), and
          // we wrap both as a 227-byte nested contract signature for the
          // Main Safe to validate via isValidSignature.
          const result =
            method === 'personal_sign'
              ? await handleDAppPersonalSign(
                  decodePersonalSignMessage(params.request.params),
                  deps,
                )
              : await handleDAppSignTypedData(
                  decodeTypedDataPayload(params.request.params),
                  deps,
                )
          console.log(
            `[SafeWard] ${method} signed off-chain — policy: ${result.policyDetail}`,
          )
          response = { result: result.outerSig }
        } else {
          response = {
            error: {
              code: 5101,
              message: `SafeWard doesn't handle ${method} in v1`,
            },
          }
        }

        await wkInstance.respondSessionRequest({
          topic,
          response: { id, jsonrpc: '2.0', ...response },
        })
        console.log(
          `[SafeWard] handler done in ${Date.now() - tApprove}ms — responded to dApp`,
        )
        wk.removeRequest(topic, id)
        void pending.refresh()
      } catch (err) {
        await wkInstance.respondSessionRequest({
          topic,
          response: {
            id,
            jsonrpc: '2.0',
            error: { code: 5000, message: (err as Error).message },
          },
        })
        wk.removeRequest(topic, id)
        throw err
      }
    },
    [walletProvider, account.address, settings, pending, wk],
  )

  const onRejectRequest = useCallback(
    async (req: WCRequest) => {
      const wkInstance = await getWalletKit()
      await wkInstance.respondSessionRequest({
        topic: req.topic,
        response: {
          id: req.id,
          jsonrpc: '2.0',
          error: { code: 5000, message: 'User rejected' },
        },
      })
      wk.removeRequest(req.topic, req.id)
    },
    [wk],
  )

  return (
    <div className="mx-auto max-w-5xl px-6 py-6">
      <Header
        health={pending.health}
        dappCount={wk.sessions.length}
        safes={
          resolved.kind === 'safeward'
            ? { mainSafe: resolved.mainSafe, moduleSafe: resolved.moduleSafe }
            : null
        }
        onRefresh={() => void pending.refresh()}
        onToggleSettings={() => setSettingsOpen((v) => !v)}
        onOpenDApps={() => setDappsOpen(true)}
      />

      {settingsOpen && (
        <SettingsCard
          settings={settings}
          onSave={onSaveSettings}
          onClose={() => setSettingsOpen(false)}
          onPing={async () => {
            try {
              const r = await new SafewardClient({
                baseUrl: settings.safewardUrl,
                authToken: settings.safewardAuthToken,
              }).health()
              return { ok: true, message: r.service }
            } catch (err) {
              return { ok: false, message: (err as Error).message }
            }
          }}
        />
      )}

      <DAppsModal
        open={dappsOpen}
        onClose={() => setDappsOpen(false)}
        sessions={wk.sessions}
        onRefresh={() => void wk.refreshSessions()}
      />

      {wk.pendingProposals.map((p) => (
        <ProposalCard
          key={p.id}
          proposal={p}
          onDone={() => wk.removeProposal(p.id)}
        />
      ))}

      {wk.pendingRequests.length > 0 && (
        <>
          <div id="incoming-anchor"></div>
          <div className="animate-pulse-bg mb-4 rounded-xl border border-l-4 border-l-sky-500 bg-sky-500/5 px-4 py-3 text-sm font-semibold">
            🔔 {wk.pendingRequests.length} incoming dApp request
            {wk.pendingRequests.length === 1 ? '' : 's'} below — approve or
            reject quickly, dApps time out in ~25s
          </div>
        </>
      )}

      {wk.pendingRequests.map((r) => (
        <RequestCard
          key={`${r.topic}-${r.id}`}
          request={r}
          signerConnected={account.isConnected && !!walletProvider}
          onApprove={() => onApproveRequest(r)}
          onReject={() => onRejectRequest(r)}
        />
      ))}

      {resolved.kind === 'landing' ? (
        <LandingCard />
      ) : resolved.kind === 'onboarding' ? (
        <OnboardingCard
          inputAddress={resolved.input}
          guardianAddress={pending.guardianAddress}
          probe={probe}
        />
      ) : (
        <>
          <SummaryCard
            health={pending.health}
            dappCount={wk.sessions.length}
            pendingRequestCount={wk.pendingRequests.length}
            lastRefreshed={pending.lastRefreshed}
          />

          <PortfolioCard
            state={portfolio.state}
            positionsState={positions.state}
            positionsOpen={positionsOpen}
            onTogglePositions={() => setPositionsOpen((v) => !v)}
          />

          <PendingTxList
            txs={pending.txs}
            decisions={pending.decisions}
            decisionErrors={pending.decisionErrors}
            guardianAddress={pending.guardianAddress}
            moduleSafe={pending.moduleSafe}
            walletProvider={walletProvider}
            signerAddress={account.address}
            onAfterSign={() => void pending.refresh()}
            safewardUrl={settings.safewardUrl}
            safewardAuthToken={settings.safewardAuthToken}
          />

          {dashboardAddress && (
            <TransactionsCard
              state={transactions.state}
              onLoadMore={() => void transactions.loadMore()}
              selfAddress={dashboardAddress}
            />
          )}
        </>
      )}

      <footer className="mt-12 text-center text-xs text-muted-foreground">
        SafeWard UI ·{' '}
        <a
          href="https://github.com/arein/safeward"
          target="_blank"
          rel="noopener"
          className="text-sky-400 hover:underline"
        >
          source
        </a>{' '}
        · MIT
      </footer>
    </div>
  )
}

/**
 * Pull the actual message string out of a personal_sign WC request. dApps
 * send params as `[message, address]` (per EIP-1474) but some pass the
 * order reversed; we accept either, picking the 0x-hex-looking one as the
 * message.
 */
function decodePersonalSignMessage(params: unknown[] | undefined): string {
  const a = params?.[0]
  const b = params?.[1]
  const looksLikeAddress = (v: unknown) =>
    typeof v === 'string' && /^0x[0-9a-fA-F]{40}$/.test(v)
  if (typeof a === 'string' && !looksLikeAddress(a)) return a
  if (typeof b === 'string') return b
  throw new Error('personal_sign: could not locate message in params')
}

function decodeTypedDataPayload(
  params: unknown[] | undefined,
): EthSignTypedDataPayload {
  // params shape: [address, payloadJsonOrObject]
  const raw = params?.[1]
  const payload =
    typeof raw === 'string' ? JSON.parse(raw) : (raw as unknown)
  if (!payload || typeof payload !== 'object') {
    throw new Error('eth_signTypedData_v4: missing typed data payload')
  }
  return payload as EthSignTypedDataPayload
}

